import { useEffect, useRef, useState } from "react";
import { blockNavigation } from "@/router";
import { Dialog } from "@/ui";

/** A dirty form owns both departure doors and renders its decision using the shared accessible dialog. */
export function useUnsavedGuard(dirty: boolean, form: string, onDiscard: () => void) {
  const latest = useRef({ dirty, onDiscard });
  latest.current = { dirty, onDiscard };
  const [pending, setPending] = useState<(() => void) | null>(null);
  // A write may finish while departure is waiting for a decision. Once the saved
  // editor is clean (or its dialog closes), there is no draft left to discard.
  useEffect(() => {
    if (!dirty && pending) {
      setPending(null);
      pending();
    }
  }, [dirty, pending]);
  useEffect(() => {
    if (!dirty) return;
    const remove = blockNavigation(proceed => latest.current.dirty ? setPending(() => proceed) : proceed());
    const unload = (event: BeforeUnloadEvent) => {
      if (!latest.current.dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", unload);
    return () => { remove(); window.removeEventListener("beforeunload", unload); };
  }, [dirty]);
  const stay = () => setPending(null);
  return {
    markClean: () => { latest.current.dirty = false; },
    confirm: (proceed: () => void) => latest.current.dirty ? setPending(() => proceed) : proceed(),
    dialog: <Dialog title="Unsaved changes" open={pending !== null} onClose={stay}>
      <p>You have unsaved changes on {form}.</p>
      <div className="row row--end" style={{ gap: 8 }}>
        <button type="button" className="btn" onClick={stay}>Stay</button>
        <button type="button" className="btn btn--primary" onClick={() => {
          latest.current.dirty = false;
          latest.current.onDiscard();
          const proceed = pending;
          setPending(null);
          proceed?.();
        }}>Discard</button>
      </div>
    </Dialog>,
  };
}
